import type {
  TaskTableColumn,
  TaskTableDefinition,
  TaskTableField,
  TaskTableFormState,
  TaskTableRow,
  TaskTableSavePayload,
} from "../types/manualAssessmentWorkflow";
import type { WorkflowTaskDetail } from "../services/regulationsService";
import { isTaskTableAttachmentField } from "./taskTableAttachment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en";

dayjs.extend(customParseFormat);

/** Save/submit wire format for Target Date (`taskDurationDate` / `dueDate`). */
export const TASK_DURATION_DATE_API_FORMAT = "DD/MMM/YYYY";

const TASK_DURATION_DATE_PARSE_FORMATS = [
  "YYYY-MM-DD",
  "DD/MMM/YYYY",
  "DD-MMM-YYYY",
  "DD/MMM/YYYY HH:mm:ss",
  "DD-MMM-YYYY HH:mm:ss",
] as const;

export const TRIAGE_TABLE_NAME = "triage_table";

export const TRIAGE_TABLE_SELECT_ALL_LABEL =
  "Select All As No Impact Assessment Required";

/** Helper copy shown below the form-level select-all toggle. */
export const TRIAGE_TABLE_SELECT_ALL_HELPER_TEXT =
  '';

/** Overall Review companion table (additional SME categories). */
export const ADD_NEW_SME_CATEGORY_TABLE_NAME =
  "Add New SME Category for Assessment";

/** Fallback description when backend omits `taskTables[].description`. */
export const ADD_NEW_SME_CATEGORY_TABLE_DESCRIPTION =
  "Select any additional SME Categories that need to complete an assessment. SME Categories not selected will not be included in this assessment round.";

/** Stored / displayed when Approve is selected (no reject-comment input). */
export const APPROVAL_COMMENTS_INACTIVE_VALUE = "-";

export const TRIAGE_TABLE_INDEX_LABEL = "SME Category";

export const TRIAGE_TABLE_FIELD_ORDER = [
  "assessment_required",
  "taskAssignee",
  "additionalRecipients",
  "reviewer",
  "dueDate",
] as const;

export type TriageTableFieldName = (typeof TRIAGE_TABLE_FIELD_ORDER)[number];

export const TRIAGE_TABLE_COLUMN_LABELS: Record<TriageTableFieldName, string> = {
  assessment_required: "Assessment Required?",
  taskAssignee: "Assign Assessor",
  additionalRecipients: "Additional recipients",
  reviewer: "Reviewer",
  dueDate: "Target Date",
};

export const TRIAGE_TABLE_SELECT_ALL_TYPOGRAPHY_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#0D0D0D",
} as const;

export type TriageTableColumnDescriptor = {
  columnKey: string;
  fieldName: string;
  label: string;
  type: string;
  sourceColumnIndex: number;
  required?: boolean;
};

/** Strip a trailing " *" from backend/mock labels when rendering a styled required marker. */
export function stripLabelRequiredAsterisk(label: string): string {
  return label.replace(/\s*\*$/, "");
}

export function getTaskTableFieldName(
  field: TaskTableField | undefined,
): string {
  if (!field) return "";
  const fromField = field.field;
  if (typeof fromField === "string" && fromField.trim()) {
    return fromField.trim();
  }
  const fromName = field.name;
  if (typeof fromName === "string" && fromName.trim()) {
    return fromName.trim();
  }
  return "";
}

function parseTaskTableBooleanish(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === "true") return true;
  if (value === false || value === 0 || value === "false") return false;
  return undefined;
}

/**
 * Table columns the grid must not render.
 * - `hide: true` is the table-column flag (SME Leaders).
 * - Do not treat `visible: false` alone as hidden: Assign To SME Leader / Reviewer
 *   are `visible: false` + `hide: false` and must stay in the grid.
 * - Sibling `taskOwners` columns also send `visible: false`; hide those too.
 */
export function isHiddenTaskTableColumn(
  column: TaskTableColumn | undefined,
): boolean {
  if (!column) return false;
  const field = column.field as Record<string, unknown> | undefined;
  const hide = parseTaskTableBooleanish(field?.hide);
  if (hide === true) return true;
  const visible = parseTaskTableBooleanish(field?.visible);
  if (
    getTaskTableFieldName(column.field) === "taskOwners" &&
    visible === false
  ) {
    return true;
  }
  return false;
}

export function getRowTaskInstanceId(row: TaskTableRow): string {
  for (const column of row.columns ?? []) {
    if (column.taskInstanceUniqueId) {
      return column.taskInstanceUniqueId;
    }
  }
  return "";
}

export function resolveColumnTaskInstanceId(
  row: TaskTableRow,
  column: TaskTableColumn,
): string {
  return column.taskInstanceUniqueId ?? getRowTaskInstanceId(row);
}

function getReferenceRow(
  table: TaskTableDefinition,
): TaskTableRow | undefined {
  const rows = table.rows ?? [];
  if (rows.length === 0) return undefined;
  return rows.reduce((best, row) =>
    (row.columns?.length ?? 0) > (best.columns?.length ?? 0) ? row : best,
  rows[0]);
}

function findIndexColumn(
  table: TaskTableDefinition,
  row: TaskTableRow,
): TaskTableColumn | undefined {
  const indexKey = table.index ?? "department";
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === indexKey,
  );
}

export function getTaskTableIndexLabel(table: TaskTableDefinition): string {
  const referenceRow = getReferenceRow(table);
  const indexColumn = referenceRow
    ? findIndexColumn(table, referenceRow)
    : undefined;
  const label = indexColumn?.field?.label?.trim();
  if (label) return label;
  if (isTriageTaskTable(table)) return TRIAGE_TABLE_INDEX_LABEL;
  const indexKey = table.index ?? "department";
  return indexKey.replace(/_/g, " ");
}

export function getTaskTableIndexDisplayValue(
  table: TaskTableDefinition,
  row: TaskTableRow,
): string {
  const indexKey = table.index ?? "department";
  const raw = row[indexKey];
  const indexColumn = findIndexColumn(table, row);
  const field = indexColumn?.field;
  if (field?.displayValue != null && String(field.displayValue).trim()) {
    return String(field.displayValue);
  }
  const optionLabel = getTriageSelectOptionLabel(field, raw);
  if (optionLabel) return optionLabel;
  return raw == null ? "" : String(raw);
}

export function findColumnByDescriptor(
  row: TaskTableRow,
  descriptor: Pick<
    TriageTableColumnDescriptor,
    "sourceColumnIndex" | "fieldName" | "label"
  >,
): TaskTableColumn | undefined {
  const byIndex = row.columns?.[descriptor.sourceColumnIndex];
  if (
    byIndex &&
    getTaskTableFieldName(byIndex.field) === descriptor.fieldName
  ) {
    const byIndexLabel = byIndex.field?.label?.trim();
    const descriptorLabel = descriptor.label?.trim();
    if (!descriptorLabel || byIndexLabel === descriptorLabel) {
      return byIndex;
    }
  }

  const descriptorLabel = descriptor.label?.trim();
  return row.columns?.find((column) => {
    if (getTaskTableFieldName(column.field) !== descriptor.fieldName) {
      return false;
    }
    if (!descriptorLabel) return true;
    return column.field?.label?.trim() === descriptorLabel;
  });
}

/**
 * Reviewer column detector for triage / Identify SME tables.
 * Do NOT treat arbitrary readonly fields (e.g. department / SME Category with
 * `editable: false`) as reviewer — that previously auto-filled the leader id
 * into `department` on save/submit.
 */
export function isTriageReviewerColumn(
  field: TaskTableField | undefined,
): boolean {
  if (!field) return false;
  const fieldName = getTaskTableFieldName(field);
  if (fieldName === "reviewer") return true;
  const label = (field.label ?? "").toLowerCase();
  if (label.includes("reviewer")) return true;
  // Backend often reuses field name `taskAssignee` for both SME Lead and Reviewer;
  // the Reviewer copy is readonly / non-editable.
  if (
    fieldName === "taskAssignee" &&
    (field.readonly === true || field.editable === false)
  ) {
    return true;
  }
  return false;
}

function findReviewerColumn(row: TaskTableRow): TaskTableColumn | undefined {
  return row.columns?.find((column) => isTriageReviewerColumn(column.field));
}

export function findTriageReviewerColumn(
  row: TaskTableRow,
): TaskTableColumn | undefined {
  return findReviewerColumn(row);
}

/** Editable taskAssignee column used to resolve reviewer display labels. */
export function findTaskAssigneeSourceColumn(
  row: TaskTableRow,
): TaskTableColumn | undefined {
  return row.columns?.find((column) => {
    const fieldName = getTaskTableFieldName(column.field);
    if (fieldName !== "taskAssignee") return false;
    return !isTriageReviewerColumn(column.field);
  });
}

type NestedTaskOwnersSource = {
  taskInstanceUniqueId?: string | null;
  taskCustomAssignee?: unknown;
  propertyValueItemList?: Array<{ field?: string; fieldValue?: unknown }>;
};

function readNestedTaskOwnersSource(
  value: unknown,
): NestedTaskOwnersSource | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  return {
    taskInstanceUniqueId:
      typeof record.taskInstanceUniqueId === "string" ||
      typeof record.taskInstanceUniqueId === "number"
        ? String(record.taskInstanceUniqueId)
        : null,
    taskCustomAssignee: record.taskCustomAssignee,
    propertyValueItemList: Array.isArray(record.propertyValueItemList)
      ? record.propertyValueItemList.filter(
          (item): item is { field?: string; fieldValue?: unknown } =>
            Boolean(item) && typeof item === "object",
        )
      : undefined,
  };
}

/**
 * Nested future-task unique id whose owners feed this cell's task-assignees
 * request. Each column uses its own nested activity (Assign To SME vs Review).
 */
export function resolveTaskTableAsyncOwnersTaskId(
  row: TaskTableRow,
  column: TaskTableColumn | undefined,
): string {
  if (!column) return getRowTaskInstanceId(row);
  return resolveColumnTaskInstanceId(row, column);
}

/**
 * Owners on the same nested task as this cell. Prefer the row's hidden
 * `taskOwners` column (new backend shape), then `taskTables[].tasks[]`.
 */
export function getTaskTableNestedTaskOwners(
  table: TaskTableDefinition,
  taskInstanceUniqueId: string,
  row?: TaskTableRow,
): unknown {
  if (!taskInstanceUniqueId) return undefined;

  const fromRow = (row?.columns ?? []).find(
    (column) =>
      String(column.taskInstanceUniqueId ?? "") === taskInstanceUniqueId &&
      getTaskTableFieldName(column.field) === "taskOwners",
  )?.field?.fieldValue;
  if (fromRow != null) return fromRow;

  if (!Array.isArray(table.tasks)) return undefined;

  for (const raw of table.tasks) {
    const task = readNestedTaskOwnersSource(raw);
    if (!task || String(task.taskInstanceUniqueId ?? "") !== taskInstanceUniqueId) {
      continue;
    }
    const fromList = task.propertyValueItemList?.find(
      (item) => item.field === "taskOwners",
    )?.fieldValue;
    if (fromList != null) return fromList;
    if (task.taskCustomAssignee != null) return task.taskCustomAssignee;
    return undefined;
  }
  return undefined;
}

/**
 * Overlay nested-task owners onto shared form state so `/pm/selection/task-assignees`
 * expands the next activity's candidates instead of the current task's.
 */
export function buildTaskTableCellAsyncFormData(
  baseFormData: Record<string, unknown> | undefined,
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn | undefined,
): Record<string, unknown> | undefined {
  const nestedOwners = getTaskTableNestedTaskOwners(
    table,
    resolveTaskTableAsyncOwnersTaskId(row, column),
    row,
  );
  if (nestedOwners === undefined) return baseFormData;
  return {
    ...(baseFormData ?? {}),
    taskOwners: nestedOwners,
  };
}

export function resolveTaskTableSelectDisplayLabel(
  options: ReadonlyArray<{ label: string; value: string | number | boolean }>,
  value: unknown,
): string {
  if (value == null || value === "") return "";
  const match = options.find((option) => String(option.value) === String(value));
  return match?.label ?? String(value);
}

export function getReviewerDisplayValue(
  table: TaskTableDefinition,
  row: TaskTableRow,
  state: TaskTableFormState,
  reviewerColumn: TaskTableColumn,
): unknown {
  const sourceColumn = findTaskAssigneeSourceColumn(row);
  if (sourceColumn) {
    const fromAssignee = getTaskTableCellValue(
      state,
      table.name,
      resolveColumnTaskInstanceId(row, sourceColumn),
      getTaskTableFieldName(sourceColumn.field),
      sourceColumn.field?.fieldValue,
    );
    if (fromAssignee != null && fromAssignee !== "") {
      return fromAssignee;
    }
  }

  return getTaskTableCellValue(
    state,
    table.name,
    resolveColumnTaskInstanceId(row, reviewerColumn),
    getTaskTableFieldName(reviewerColumn.field),
    reviewerColumn.field?.fieldValue,
  );
}

export function isTriageTaskTable(table: TaskTableDefinition): boolean {
  return table.name === TRIAGE_TABLE_NAME;
}

function findAssessmentRequiredColumn(
  row: TaskTableRow,
): TaskTableColumn | undefined {
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === "assessment_required",
  );
}

/** Tables with per-row assessment_required toggle (triage_table or backend equivalent). */
export function tableSupportsAssessmentRequiredRows(
  table: TaskTableDefinition,
): boolean {
  if (isTriageTaskTable(table)) return true;
  return (table.rows ?? []).some((row) =>
    Boolean(findAssessmentRequiredColumn(row)),
  );
}

/** Stable state bucket for virtual assessment_required (null taskInstanceUniqueId). */
export function getTriageAssessmentRequiredStateKey(
  table: TaskTableDefinition,
  row: TaskTableRow,
): string {
  const column = findAssessmentRequiredColumn(row);
  const resolved = column
    ? resolveColumnTaskInstanceId(row, column)
    : "";
  if (resolved) return resolved;

  const indexValue = getTaskTableIndexValue(table, row);
  return indexValue ? `row:${indexValue}` : "__virtual_row__";
}

export function getTriageAssessmentRequiredValue(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  row: TaskTableRow,
): boolean {
  const column = findAssessmentRequiredColumn(row);
  if (!column) return true;

  const stateKey = getTriageAssessmentRequiredStateKey(table, row);
  const fromState = state[table.name]?.[stateKey]?.assessment_required;
  if (fromState !== undefined) {
    return isAssessmentRequiredValue(fromState);
  }

  return resolveAssessmentRequiredInitialValue(column.field);
}

export function coerceBooleanFieldValue(
  value: unknown,
  fallback = false,
): boolean {
  if (value === true) return true;
  if (value === false) return false;
  if (value == null || value === "") return fallback;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }
  return Boolean(value);
}

export function isToggleField(field: TaskTableField | undefined): boolean {
  if (!field) return false;
  const fieldType = String(field.type ?? "").toLowerCase();
  return (
    fieldType === "toggle" ||
    getTaskTableFieldName(field) === "assessment_required"
  );
}

function isAssessmentRequiredValue(value: unknown): boolean {
  return coerceBooleanFieldValue(value, true);
}

/**
 * Resolve assessment_required when state has no value yet.
 * Priority: explicit `fieldValue` → schema `default` → AC-01 ON.
 */
export function resolveAssessmentRequiredInitialValue(
  field?: TaskTableField,
): boolean {
  if (hasAssessmentRequiredBackendValue(field?.fieldValue)) {
    return coerceAssessmentRequiredStoredValue(field?.fieldValue);
  }
  if (hasAssessmentRequiredBackendValue(field?.default)) {
    return coerceAssessmentRequiredStoredValue(field?.default);
  }
  return true;
}

function hasAssessmentRequiredBackendValue(value: unknown): boolean {
  return value !== undefined && value !== null;
}

/** Normalize backend/state booleans for assessment_required storage. */
function coerceAssessmentRequiredStoredValue(value: unknown): boolean {
  return coerceBooleanFieldValue(value, true);
}

/** Switch is on when every row is marked as no impact (assessment not required). */
export function getTriageSelectAllSwitchState(
  table: TaskTableDefinition,
  state: TaskTableFormState,
): { checked: boolean } {
  const rows = table.rows ?? [];
  if (rows.length === 0) {
    return { checked: false };
  }

  for (const row of rows) {
    if (isTriageRowAssessmentRequired(table, state, row)) {
      return { checked: false };
    }
  }

  return { checked: true };
}

/** Toggle all rows to no-impact (assessment_required=false) or required (true). */
export function applyTriageSelectAllNoImpact(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  noImpactForAll: boolean,
): TaskTableFormState {
  let next = state;
  const assessmentRequiredValue = !noImpactForAll;

  for (const row of table.rows ?? []) {
    const column = findAssessmentRequiredColumn(row);
    const fieldName = getTaskTableFieldName(column?.field);
    if (!column || fieldName !== "assessment_required") continue;
    next = setTaskTableCellValue(
      next,
      table.name,
      getTriageAssessmentRequiredStateKey(table, row),
      fieldName,
      assessmentRequiredValue,
    );
  }

  return next;
}

export function getTriageRowTaskInstanceId(row: TaskTableRow): string {
  const column = findAssessmentRequiredColumn(row);
  if (column) {
    const resolved = resolveColumnTaskInstanceId(row, column);
    if (resolved) return resolved;
  }
  return getRowTaskInstanceId(row);
}

export function isTriageRowAssessmentRequired(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  row: TaskTableRow,
): boolean {
  return getTriageAssessmentRequiredValue(table, state, row);
}

export function sortTriageTableDefinition(
  table: TaskTableDefinition,
): TaskTableDefinition {
  const indexKey = table.index ?? "department";
  const rows = [...(table.rows ?? [])].sort((left, right) =>
    String(left[indexKey] ?? "").localeCompare(
      String(right[indexKey] ?? ""),
      undefined,
      { sensitivity: "base" },
    ),
  );
  return { ...table, rows };
}

export function normalizeTaskTables(
  tables: readonly TaskTableDefinition[],
): TaskTableDefinition[] {
  return tables.map((table) =>
    isTriageTaskTable(table) ? sortTriageTableDefinition(table) : table,
  );
}

export function getTriageSelectOptionLabel(
  field: TaskTableField | undefined,
  value: unknown,
): string {
  if (value == null || value === "") return "";
  const options = field?.options ?? [];
  const match = options.find((option) => String(option.value) === String(value));
  return match?.label ?? String(value);
}

/** Update triage cell; keep reviewer mirrored to assign assessor, including clear. */
export function applyTriageTableCellUpdate(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
  value: unknown,
): TaskTableFormState {
  const taskInstanceUniqueId =
    fieldName === "assessment_required"
      ? getTriageAssessmentRequiredStateKey(table, row)
      : resolveColumnTaskInstanceId(row, column);
  let next = setTaskTableCellValue(
    state,
    table.name,
    taskInstanceUniqueId,
    fieldName,
    value,
  );

  if (
    fieldName === "taskAssignee" &&
    !isTriageReviewerColumn(column.field)
  ) {
    const reviewerColumn = findReviewerColumn(row);
    const reviewerFieldName = getTaskTableFieldName(reviewerColumn?.field);
    if (reviewerColumn && reviewerFieldName) {
      const reviewerValue = value == null || value === "" ? null : value;
      next = setTaskTableCellValue(
        next,
        table.name,
        resolveColumnTaskInstanceId(row, reviewerColumn),
        reviewerFieldName,
        reviewerValue,
      );
    }
  }

  return next;
}

function findApprovalCommentsColumn(
  row: TaskTableRow,
): TaskTableColumn | undefined {
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === "approvalComments",
  );
}

function findApproveColumn(
  row: TaskTableRow,
): TaskTableColumn | undefined {
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === "approve",
  );
}

/** True when approval result is Reject (matches backend `show.startwiths: "reject"`). */
export function isRejectApprovalResult(value: unknown): boolean {
  if (value == null || value === "") return false;
  return String(value).trim().toLowerCase().startsWith("reject");
}

type TaskTableFieldShowRule = {
  when?: string;
  /** Backend typo for startsWith (e.g. approvalComments.show). */
  startwiths?: string;
  startsWith?: string;
  eq?: string;
};

/**
 * Evaluate BPMN `field.show` against a dependency value.
 * Example: `{ when: "approve", startwiths: "reject" }` → true when value starts with "reject".
 */
export function matchesTaskTableFieldShowRule(
  show: unknown,
  dependencyValue: unknown,
): boolean | null {
  if (!show || typeof show !== "object") return null;
  const rule = show as TaskTableFieldShowRule;
  const prefix = rule.startwiths ?? rule.startsWith;
  if (prefix != null && String(prefix).length > 0) {
    const dep = dependencyValue == null ? "" : String(dependencyValue);
    return dep.toLowerCase().startsWith(String(prefix).toLowerCase());
  }
  if (rule.eq != null) {
    return String(dependencyValue ?? "") === String(rule.eq);
  }
  return null;
}

/**
 * Whether Reject Comments should render as an input.
 * Schema example:
 *   show: { when: "approve", startwiths: "reject" }
 * → input only when the `approve` cell value starts with "reject"; otherwise "-".
 */
export function shouldShowApprovalCommentsInput(
  field: TaskTableField | undefined,
  approveValue: unknown,
): boolean {
  const show = field?.show;
  if (show && typeof show === "object") {
    const rule = show as TaskTableFieldShowRule;
    // `when` names the dependency field (always "approve" for Reject Comments).
    const whenField =
      typeof rule.when === "string" && rule.when.trim()
        ? rule.when.trim()
        : "approve";
    if (whenField !== "approve") {
      // Unexpected dependency — fall back to reject-prefix check on the value we have.
      return isRejectApprovalResult(approveValue);
    }
    const fromShow = matchesTaskTableFieldShowRule(show, approveValue);
    if (fromShow != null) return fromShow;
  }
  return isRejectApprovalResult(approveValue);
}

export function getRowApproveValue(
  table: TaskTableDefinition,
  row: TaskTableRow,
  state: TaskTableFormState,
): unknown {
  const approveColumn = findApproveColumn(row);
  if (!approveColumn) return undefined;
  return getTaskTableCellValue(
    state,
    table.name,
    resolveColumnTaskInstanceId(row, approveColumn),
    "approve",
    approveColumn.field?.fieldValue,
  );
}

/**
 * Overall Review approve table: changing Approve/Reject drives Reject Comments.
 * Approve → store "-" and hide input; Reject → clear dash placeholder for editing.
 */
export function applyOverallApproveTableCellUpdate(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
  value: unknown,
): TaskTableFormState {
  const taskInstanceUniqueId = resolveColumnTaskInstanceId(row, column);
  let next = setTaskTableCellValue(
    state,
    table.name,
    taskInstanceUniqueId,
    fieldName,
    value,
  );

  if (fieldName !== "approve") return next;

  const commentsColumn = findApprovalCommentsColumn(row);
  if (!commentsColumn) return next;

  const commentsFieldName = getTaskTableFieldName(commentsColumn.field);
  if (!commentsFieldName) return next;

  const commentsTaskId = resolveColumnTaskInstanceId(row, commentsColumn);
  if (shouldShowApprovalCommentsInput(commentsColumn.field, value)) {
    const current = getTaskTableCellValue(
      next,
      table.name,
      commentsTaskId,
      commentsFieldName,
      commentsColumn.field?.fieldValue,
    );
    if (
      current === APPROVAL_COMMENTS_INACTIVE_VALUE ||
      current === "—"
    ) {
      next = setTaskTableCellValue(
        next,
        table.name,
        commentsTaskId,
        commentsFieldName,
        "",
      );
    }
  } else {
    next = setTaskTableCellValue(
      next,
      table.name,
      commentsTaskId,
      commentsFieldName,
      APPROVAL_COMMENTS_INACTIVE_VALUE,
    );
  }

  return next;
}

/** Title + helper for assessment_required tables.
 * - With sibling `SME Assessment by Category`: companion title, no select-all switch.
 * - Otherwise (single triage-style table): “Select all…” + switch.
 */
export function getAssessmentRequiredTableHeader(
  table: TaskTableDefinition,
  options?: { hasSmeAssessmentByCategoryTable?: boolean },
): {
  title: string;
  description: string;
  showSelectAllSwitch: boolean;
} {
  const hasSmeAssessmentByCategory =
    options?.hasSmeAssessmentByCategoryTable === true;

  if (hasSmeAssessmentByCategory && !isTriageTaskTable(table)) {
    const name = table.name?.trim() || ADD_NEW_SME_CATEGORY_TABLE_NAME;
    const fromApi =
      typeof table.description === "string" ? table.description.trim() : "";
    return {
      title: name,
      description: fromApi || ADD_NEW_SME_CATEGORY_TABLE_DESCRIPTION,
      showSelectAllSwitch: false,
    };
  }

  return {
    title: TRIAGE_TABLE_SELECT_ALL_LABEL,
    description: TRIAGE_TABLE_SELECT_ALL_HELPER_TEXT,
    showSelectAllSwitch: true,
  };
}

export function getMinimumTargetDateIso(referenceDate: Date = new Date()): string {
  const year = referenceDate.getFullYear();
  const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
  const day = String(referenceDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Local calendar today as Plan & Track `DD/MMM/YYYY` (English month).
 * CustomDateWidget only parses this API format (not ISO).
 */
export function getMinimumSelectableDateApi(
  referenceDate: Date = new Date(),
): string {
  return dayjs(referenceDate).locale("en").format(TASK_DURATION_DATE_API_FORMAT);
}

export function isManualAssessmentDateField(
  fieldName: string,
  type?: unknown,
): boolean {
  const name = fieldName.trim();
  if (name === "taskDurationDate" || name === "dueDate") return true;
  return String(type ?? "").trim().toLowerCase() === "date";
}

export function parseManualAssessmentDate(value: unknown): dayjs.Dayjs | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const strictParsed = dayjs(
    raw,
    [...TASK_DURATION_DATE_PARSE_FORMATS],
    "en",
    true,
  );
  if (strictParsed.isValid()) return strictParsed;

  const loose = dayjs(raw);
  return loose.isValid() ? loose : null;
}

/** HTML date input value (`YYYY-MM-DD`). Empty when the raw value cannot be parsed. */
export function formatTaskDurationDateForInput(value: unknown): string {
  const parsed = parseManualAssessmentDate(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
}

/** Save/submit value: `23/Sep/2026`. Leaves null/empty unchanged; unknown strings pass through. */
export function formatTaskDurationDateForApi(value: unknown): unknown {
  if (value == null || value === "") return value;
  const parsed = parseManualAssessmentDate(value);
  return parsed
    ? parsed.locale("en").format(TASK_DURATION_DATE_API_FORMAT)
    : value;
}

export function isTargetDateBeforeMinimum(
  value: unknown,
  minimumIso = getMinimumTargetDateIso(),
): boolean {
  const parsed = parseManualAssessmentDate(value);
  if (!parsed) return false;
  const minimum = parseManualAssessmentDate(minimumIso);
  if (!minimum) return false;
  return parsed.isBefore(minimum, "day");
}

export type TaskTableCellValidationError = {
  tableName: string;
  taskInstanceUniqueId: string;
  fieldName: string;
  message: string;
};

export function buildTaskTableCellValidationErrorKey(
  tableName: string,
  taskInstanceUniqueId: string,
  fieldName: string,
): string {
  return `${tableName}::${taskInstanceUniqueId}::${fieldName}`;
}

function tableHasApproveDecisionField(table: TaskTableDefinition): boolean {
  return (table.rows ?? []).some((row) => Boolean(findApproveColumn(row)));
}

/** True when value is a concrete Approve/Reject decision (not placeholder/empty). */
export function isApproveDecisionSelected(value: unknown): boolean {
  if (value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized.startsWith("approve") || normalized.startsWith("reject");
}

function isEmptyRequiredCellValue(value: unknown): boolean {
  return value == null || value === "";
}

function isMissingRequiredCellValue(
  fieldName: string,
  cellValue: unknown,
): boolean {
  if (fieldName === "approve") {
    return !isApproveDecisionSelected(cellValue);
  }
  if (fieldName === "approvalComments") {
    return (
      isEmptyRequiredCellValue(cellValue) ||
      isInactiveApprovalCommentsValue(cellValue)
    );
  }
  return isEmptyRequiredCellValue(cellValue);
}

function shouldValidateTaskTableField(
  table: TaskTableDefinition,
  row: TaskTableRow,
  field: TaskTableField | undefined,
  fieldName: string,
  indexKey: string,
  state: TaskTableFormState,
): boolean {
  if (!fieldName || fieldName === indexKey) return false;
  // Virtual fields are usually not form inputs (e.g. assessment_required
  // toggle). Exception: Overall Review / SME approve select is virtual
  // but must be chosen before submit.
  if (isVirtualField(field) && fieldName !== "approve") return false;
  if (
    fieldName === "approvalComments" &&
    !shouldShowApprovalCommentsInput(
      field,
      getRowApproveValue(table, row, state),
    )
  ) {
    return false;
  }
  // Always require Approve/Reject; Reject Reason is required when visible
  // (Reject selected). Also honor schema `required`.
  return (
    Boolean(field?.required) ||
    fieldName === "approve" ||
    fieldName === "approvalComments"
  );
}

function collectRequiredCellError(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn,
  field: TaskTableField,
  fieldName: string,
  state: TaskTableFormState,
  minimumDate: string,
): TaskTableCellValidationError | null {
  const label = field.label?.trim() || fieldName;
  const taskInstanceUniqueId = resolveColumnTaskInstanceId(row, column);
  const cellValue = getTaskTableCellValue(
    state,
    table.name,
    taskInstanceUniqueId,
    fieldName,
    field.fieldValue,
  );

  if (isMissingRequiredCellValue(fieldName, cellValue)) {
    return {
      tableName: table.name,
      taskInstanceUniqueId,
      fieldName,
      message: `${label} is required.`,
    };
  }

  const fieldType = String(field.type ?? "").toLowerCase();
  if (
    (fieldType === "date" ||
      fieldName === "dueDate" ||
      fieldName === "taskDurationDate") &&
    isTargetDateBeforeMinimum(cellValue, minimumDate)
  ) {
    return {
      tableName: table.name,
      taskInstanceUniqueId,
      fieldName,
      message: `${label} cannot be earlier than today.`,
    };
  }

  return null;
}

/**
 * Validate required editable cells before submit.
 * - Triage / assessment_required tables: active rows only (existing behavior).
 * - Approve tables (e.g. SME Assessment by Category): every row must choose
 *   Approve or Reject on the virtual `approve` field (otherwise empty submit
 *   silently becomes `approve_all`). When Reject is selected, that row's
 *   Reject Reason (`approvalComments`) is also required.
 */
export function validateTriageTaskTables(
  tables: readonly TaskTableDefinition[],
  state: TaskTableFormState,
): TaskTableCellValidationError[] {
  const errors: TaskTableCellValidationError[] = [];
  const minimumDate = getMinimumTargetDateIso();

  for (const table of tables) {
    const isAssessmentRequiredTable = tableSupportsAssessmentRequiredRows(table);
    const isApproveTable = tableHasApproveDecisionField(table);
    if (!isAssessmentRequiredTable && !isApproveTable) continue;

    const indexKey = table.index ?? "department";

    for (const row of table.rows ?? []) {
      if (
        isAssessmentRequiredTable &&
        !isTriageRowAssessmentRequired(table, state, row)
      ) {
        continue;
      }

      for (const column of row.columns ?? []) {
        const field = column.field;
        if (!field) continue;
        const fieldName = getTaskTableFieldName(field);
        if (
          !shouldValidateTaskTableField(
            table,
            row,
            field,
            fieldName,
            indexKey,
            state,
          )
        ) {
          continue;
        }

        const error = collectRequiredCellError(
          table,
          row,
          column,
          field,
          fieldName,
          state,
          minimumDate,
        );
        if (error) errors.push(error);
      }
    }
  }

  return errors;
}

function getTriageFieldRequiredFromTable(
  table: TaskTableDefinition,
  fieldName: string,
): boolean {
  for (const row of table.rows ?? []) {
    const column = row.columns?.find(
      (item) => getTaskTableFieldName(item.field) === fieldName,
    );
    if (column?.field?.required === true) {
      return true;
    }
  }
  return false;
}

function getTriageFieldTypeFromTable(
  table: TaskTableDefinition,
  fieldName: string,
): string {
  for (const row of table.rows ?? []) {
    const column = row.columns?.find(
      (item) => getTaskTableFieldName(item.field) === fieldName,
    );
    const fieldType = column?.field?.type;
    if (fieldType) {
      return String(fieldType);
    }
  }

  const defaultTypes: Record<string, string> = {
    assessment_required: "checkbox",
    taskAssignee: "select",
    additionalRecipients: "select",
    notifyAdditionalRecipients: "select",
    reviewer: "select",
    dueDate: "date",
    taskDurationDate: "date",
  };
  return defaultTypes[fieldName] ?? "text";
}

function fallbackTriageColumnDescriptors(
  table: TaskTableDefinition,
): TriageTableColumnDescriptor[] {
  return TRIAGE_TABLE_FIELD_ORDER.map((fieldName, sourceColumnIndex) => ({
    columnKey: `${fieldName}::${sourceColumnIndex}`,
    fieldName,
    label: TRIAGE_TABLE_COLUMN_LABELS[fieldName],
    type: getTriageFieldTypeFromTable(table, fieldName),
    sourceColumnIndex,
    ...(getTriageFieldRequiredFromTable(table, fieldName) ? { required: true } : {}),
  }));
}

export function collectTriageColumnDescriptors(
  table: TaskTableDefinition,
): TriageTableColumnDescriptor[] {
  const indexKey = table.index ?? "department";
  const referenceRow = getReferenceRow(table);
  const columns = referenceRow?.columns ?? [];
  if (columns.length === 0) {
    return fallbackTriageColumnDescriptors(table);
  }

  const descriptors = columns
    .map((column, sourceColumnIndex) => {
      const fieldName = getTaskTableFieldName(column.field);
      if (!fieldName || fieldName === indexKey) return null;
      if (isHiddenTaskTableColumn(column)) return null;
      const label =
        column.field?.label?.trim() ||
        TRIAGE_TABLE_COLUMN_LABELS[fieldName as TriageTableFieldName] ||
        fieldName;
      return {
        columnKey: `${fieldName}::${sourceColumnIndex}`,
        fieldName,
        label,
        type: String(
          column.field?.type ?? getTriageFieldTypeFromTable(table, fieldName),
        ),
        sourceColumnIndex,
        ...(column.field?.required === true ? { required: true } : {}),
      };
    })
    .filter((descriptor): descriptor is TriageTableColumnDescriptor =>
      Boolean(descriptor),
    );

  return descriptors.length > 0
    ? descriptors
    : fallbackTriageColumnDescriptors(table);
}

function isVirtualField(field: TaskTableField | undefined): boolean {
  return field?.is_virtual === true;
}

function isFieldDisabled(field: TaskTableField | undefined): boolean {
  if (!field) return true;
  if (field.editable === false) return true;
  if (field.readonly === true || field.disabled === true) return true;
  return false;
}

/** Build initial edit state from backend taskTables. */
export function buildInitialTaskTableState(
  tables: readonly TaskTableDefinition[] | null | undefined,
): TaskTableFormState {
  const state: TaskTableFormState = {};
  for (const table of tables ?? []) {
    const tableName = table.name;
    if (!tableName) continue;
    state[tableName] = {};
    const supportsAssessmentToggle = tableSupportsAssessmentRequiredRows(table);
    for (const row of table.rows ?? []) {
      const rowTaskId = getRowTaskInstanceId(row);
      for (const column of row.columns ?? []) {
        const field = column.field;
        const fieldName = getTaskTableFieldName(field);
        if (!fieldName) continue;

        const taskId =
          fieldName === "assessment_required"
            ? getTriageAssessmentRequiredStateKey(table, row)
            : (column.taskInstanceUniqueId ?? rowTaskId);
        if (!taskId) continue;

        if (!state[tableName][taskId]) {
          state[tableName][taskId] = {};
        }
        if (supportsAssessmentToggle && fieldName === "assessment_required") {
          // Priority: explicit fieldValue → schema default → AC-01 ON.
          state[tableName][taskId][fieldName] =
            resolveAssessmentRequiredInitialValue(field);
        } else if (field.fieldValue !== undefined) {
          state[tableName][taskId][fieldName] = field.fieldValue;
        } else if (field.default !== undefined) {
          state[tableName][taskId][fieldName] = field.default;
        }
      }
    }
  }
  return state;
}

export function getTaskTableCellValue(
  state: TaskTableFormState,
  tableName: string,
  taskInstanceUniqueId: string,
  fieldName: string,
  fallback?: unknown,
): unknown {
  const fromState = state[tableName]?.[taskInstanceUniqueId]?.[fieldName];
  return fromState !== undefined ? fromState : fallback;
}

export function setTaskTableCellValue(
  state: TaskTableFormState,
  tableName: string,
  taskInstanceUniqueId: string,
  fieldName: string,
  value: unknown,
): TaskTableFormState {
  const next: TaskTableFormState = { ...state };
  next[tableName] = { ...(next[tableName] ?? {}) };
  next[tableName][taskInstanceUniqueId] = {
    ...(next[tableName][taskInstanceUniqueId] ?? {}),
    [fieldName]: value,
  };
  return next;
}

/** Overall Review decision fields that are `is_virtual` but must still be saved. */
function isSaveableVirtualDecisionField(fieldName: string): boolean {
  return (
    fieldName === "approve" ||
    fieldName === "assessment_required" ||
    fieldName === "reopen_required"
  );
}

function resolveVirtualDecisionSaveTaskId(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
): string {
  if (fieldName === "assessment_required") {
    return getTriageAssessmentRequiredStateKey(table, row);
  }
  return resolveColumnTaskInstanceId(row, column);
}

function resolveRowFallbackTaskDefinitionId(row: TaskTableRow): string | undefined {
  for (const column of row.columns ?? []) {
    if (column.taskDefinitionId) {
      return String(column.taskDefinitionId);
    }
  }
  return undefined;
}

type TaskTableSaveContext = {
  table: TaskTableDefinition;
  state: TaskTableFormState;
  hasOverallApproveTable: boolean;
  isApproveTable: boolean;
  isOverallReviewCompanion: boolean;
  attachDepartment: boolean;
};

function shouldIncludeVirtualDecisionField(
  fieldName: string,
  ctx: Pick<
    TaskTableSaveContext,
    "isApproveTable" | "hasOverallApproveTable" | "isOverallReviewCompanion"
  >,
): boolean {
  if (fieldName === "approve") return ctx.isApproveTable;
  if (fieldName === "reopen_required") return ctx.hasOverallApproveTable;
  if (fieldName === "assessment_required") return ctx.isOverallReviewCompanion;
  return false;
}

function shouldSkipInactiveAssessmentRow(
  assessmentRequired: boolean,
  ctx: Pick<TaskTableSaveContext, "isOverallReviewCompanion"> & {
    isTriage: boolean;
    isAssessmentTable: boolean;
  },
): boolean {
  if (assessmentRequired) return false;
  // Overall Review companion must still emit assessment_required: false.
  if (ctx.isOverallReviewCompanion) return false;
  return ctx.isTriage || ctx.isAssessmentTable;
}

function resolveSaveColumnTaskId(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
  includeVirtualDecision: boolean,
): string {
  if (column.taskInstanceUniqueId) {
    return String(column.taskInstanceUniqueId);
  }
  if (!includeVirtualDecision) return "";
  return resolveVirtualDecisionSaveTaskId(table, row, column, fieldName);
}

function resolveSaveCellStateKey(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
  taskId: string,
): string {
  if (fieldName === "assessment_required") {
    return getTriageAssessmentRequiredStateKey(table, row);
  }
  if (fieldName === "approve") {
    return resolveColumnTaskInstanceId(row, column);
  }
  return taskId;
}

function isInactiveApprovalCommentsValue(value: unknown): boolean {
  return value === APPROVAL_COMMENTS_INACTIVE_VALUE || value === "—";
}

function rememberTaskDefinitionId(
  taskDefinitionIdByTaskId: Map<string, string>,
  taskId: string,
  column: TaskTableColumn,
  row: TaskTableRow,
  includeVirtualDecision: boolean,
): void {
  if (column.taskDefinitionId) {
    taskDefinitionIdByTaskId.set(taskId, String(column.taskDefinitionId));
    return;
  }
  if (!includeVirtualDecision || taskDefinitionIdByTaskId.has(taskId)) return;
  const fallbackDefinitionId = resolveRowFallbackTaskDefinitionId(row);
  if (fallbackDefinitionId) {
    taskDefinitionIdByTaskId.set(taskId, fallbackDefinitionId);
  }
}

function appendTaskTableSaveField(
  ctx: TaskTableSaveContext,
  row: TaskTableRow,
  column: TaskTableColumn,
  assessmentRequired: boolean,
  department: string,
  byTaskId: Map<string, Record<string, unknown>>,
  taskDefinitionIdByTaskId: Map<string, string>,
  departmentByTaskId: Map<string, string>,
): void {
  const { table, state } = ctx;
  const field = column.field;
  const fieldName = getTaskTableFieldName(field);
  if (!fieldName) return;
  // SME Assessment attachments are read-only on Overall Review — do not write back.
  if (isTaskTableAttachmentField(field)) return;

  const isDecisionVirtual =
    isVirtualField(field) && isSaveableVirtualDecisionField(fieldName);
  const includeVirtualDecision =
    isDecisionVirtual && shouldIncludeVirtualDecisionField(fieldName, ctx);

  // Omit other virtual fields; triage assessment_required stays out of
  // customPropertyMap (drives top-level taskResult instead).
  if (isVirtualField(field) && !includeVirtualDecision) return;

  // Overall Review companion: when switch is off, only send assessment_required.
  if (
    ctx.isOverallReviewCompanion &&
    !assessmentRequired &&
    fieldName !== "assessment_required"
  ) {
    return;
  }

  const taskId = resolveSaveColumnTaskId(
    table,
    row,
    column,
    fieldName,
    includeVirtualDecision,
  );
  if (!taskId) return;

  rememberTaskDefinitionId(
    taskDefinitionIdByTaskId,
    taskId,
    column,
    row,
    includeVirtualDecision,
  );

  const stateKey = resolveSaveCellStateKey(
    table,
    row,
    column,
    fieldName,
    taskId,
  );
  const value = getTaskTableCellValue(
    state,
    table.name,
    stateKey,
    fieldName,
    fieldName === "assessment_required"
      ? resolveAssessmentRequiredInitialValue(field)
      : field.fieldValue,
  );
  if (value === undefined) return;
  if (fieldName === "approvalComments" && isInactiveApprovalCommentsValue(value)) {
    return;
  }

  const serializedValue = isManualAssessmentDateField(fieldName, field?.type)
    ? formatTaskDurationDateForApi(value)
    : value;

  const existing = byTaskId.get(taskId) ?? {};
  existing[fieldName] = serializedValue;
  byTaskId.set(taskId, existing);

  if (ctx.attachDepartment && department) {
    departmentByTaskId.set(taskId, department);
  }
}

function buildFieldsValueMapEntries(
  byTaskId: Map<string, Record<string, unknown>>,
  taskDefinitionIdByTaskId: Map<string, string>,
  departmentByTaskId: Map<string, string>,
): TaskTableSavePayload["fieldsValueMap"] {
  return [...byTaskId.entries()].map(
    ([taskInstanceUniqueId, customPropertyMap]) => {
      const taskDefinitionId = taskDefinitionIdByTaskId.get(taskInstanceUniqueId);
      const department = departmentByTaskId.get(taskInstanceUniqueId);
      const withDepartment = department ? { department } : {};
      if (taskDefinitionId) {
        return {
          taskDefinitionId,
          ...withDepartment,
          customPropertyMap,
        };
      }
      return {
        taskInstanceUniqueId,
        ...withDepartment,
        customPropertyMap,
      };
    },
  );
}

function buildSingleTaskTableSavePayload(
  table: TaskTableDefinition,
  state: TaskTableFormState,
  hasOverallApproveTable: boolean,
): TaskTableSavePayload | null {
  const tableName = table.name;
  if (!tableName) return null;

  const isTriage = isTriageTaskTable(table);
  const isAssessmentTable = tableSupportsAssessmentRequiredRows(table);
  const isApproveTable = tableHasApproveDecisionField(table);
  const isOverallReviewCompanion =
    hasOverallApproveTable && isAssessmentTable && !isApproveTable;
  const ctx: TaskTableSaveContext = {
    table,
    state,
    hasOverallApproveTable,
    isApproveTable,
    isOverallReviewCompanion,
    attachDepartment: isApproveTable || isOverallReviewCompanion,
  };

  const byTaskId = new Map<string, Record<string, unknown>>();
  const taskDefinitionIdByTaskId = new Map<string, string>();
  const departmentByTaskId = new Map<string, string>();

  for (const row of table.rows ?? []) {
    const assessmentRequired = isAssessmentTable
      ? isTriageRowAssessmentRequired(table, state, row)
      : true;

    if (
      shouldSkipInactiveAssessmentRow(assessmentRequired, {
        isTriage,
        isAssessmentTable,
        isOverallReviewCompanion,
      })
    ) {
      continue;
    }

    const department = getTaskTableIndexValue(table, row);
    for (const column of row.columns ?? []) {
      appendTaskTableSaveField(
        ctx,
        row,
        column,
        assessmentRequired,
        department,
        byTaskId,
        taskDefinitionIdByTaskId,
        departmentByTaskId,
      );
    }
  }

  if (byTaskId.size === 0) return null;

  return {
    name: tableName,
    tasks: table.tasks ?? [],
    fieldsValueMap: buildFieldsValueMapEntries(
      byTaskId,
      taskDefinitionIdByTaskId,
      departmentByTaskId,
    ),
  };
}

/** Aggregate editable table values for save/submit (includes Overall Review virtual decisions). */
export function buildTaskTableSavePayloads(
  tables: readonly TaskTableDefinition[] | null | undefined,
  state: TaskTableFormState,
): TaskTableSavePayload[] {
  const tableList = tables ?? [];
  // Overall Review: approve-style table + companion assessment_required table.
  const hasOverallApproveTable = tableList.some((table) =>
    tableHasApproveDecisionField(table),
  );

  const payloads: TaskTableSavePayload[] = [];
  for (const table of tableList) {
    const payload = buildSingleTaskTableSavePayload(
      table,
      state,
      hasOverallApproveTable,
    );
    if (payload) payloads.push(payload);
  }
  return payloads;
}

export function getTaskTableIndexValue(
  table: TaskTableDefinition,
  row: TaskTableDefinition["rows"][number],
): string {
  const indexKey = table.index ?? "department";
  const raw = row[indexKey];
  return raw == null ? "" : String(raw);
}

export { isFieldDisabled, isVirtualField };

export function parseTaskTablesFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): TaskTableDefinition[] {
  const raw = taskDetail?.taskTables ?? (taskDetail as { taskTables?: unknown })?.taskTables;
  if (!Array.isArray(raw)) return [];
  const tables = raw.filter(
    (table): table is TaskTableDefinition =>
      Boolean(table) &&
      typeof table === "object" &&
      typeof (table as TaskTableDefinition).name === "string" &&
      Array.isArray((table as TaskTableDefinition).rows),
  );
  return normalizeTaskTables(tables);
}
